import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

// Enable httpOnly cookie auth globally
axios.defaults.withCredentials = true;
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import InvoicesTab from './InvoicesTab';
import ReceiptsTab from './ReceiptsTab';
import MileageTab from './MileageTab';
import ImportTab from './ImportTab';
import TaxTab from './TaxTab';
import PayrollTab from './PayrollTab';
import TeamTab from './TeamTab';
import OverviewTab from './OverviewTab';
import AdminTab from './AdminTab';
import PWAInstallPrompt from './PWAInstallPrompt';
import BackOnTrackTab from './BackOnTrackTab';
import ClientPortalTab from './ClientPortalTab';
import ProfileDialog from './ProfileDialog';
import FinanceBotWidget from './FinanceBotWidget';
import { AddExpenseDialog, AddIncomeDialog, EditTransactionDialog } from './TransactionDialogs';
import ShareDialog from './ShareDialog';
import { UpgradeDialog, ConvertDemoDialog } from './UpgradeDialogs';
import ExpensesListTab from './ExpensesListTab';
import IncomeListTab from './IncomeListTab';
import PnLReportsTab from './PnLReportsTab';
import ForecastTab from './ForecastTab';
import CFOTab from './CFOTab';
import { 
  Building2, 
  Plus, 
  TrendingUp, 
  TrendingDown,
  FileText,
  PieChart,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Link2,
  LogOut,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  X,
  Calendar,
  BarChart3,
  BookOpen,
  Car,
  Upload,
  Receipt,
  Lock,
  Crown,
  Pencil,
  DollarSign,
  User,
  Shield,
  History,
  Clock,
  Globe,
  Sparkles,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MyBookieDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check if we're on MyBookie domain for clean URLs
const isMyBookieDomain = () => {
    const hostname = window.location.hostname;
    return !hostname.includes('repti-sitter') && !hostname.includes('reptisitter');
  };
  
  const getLandingPath = () => {
    return isMyBookieDomain() ? '/' : '/mybookie';
  };
  
  const [activeTab, setActiveTab] = useState('overview');
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [categories, setCategories] = useState({ expense_categories: [], income_categories: [] });
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [pnlReport, setPnlReport] = useState(null);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [cashflowForecast, setCashflowForecast] = useState(null);
  const [recurringItems, setRecurringItems] = useState({ recurring_expenses: [], recurring_income: [] });
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showAddBusiness, setShowAddBusiness] = useState(false);
  const [showEditBusiness, setShowEditBusiness] = useState(false);
  const [editBusinessName, setEditBusinessName] = useState('');
  const [editBusinessNotifyEmail, setEditBusinessNotifyEmail] = useState('');
  const [sendingBrief, setSendingBrief] = useState(false);
  // Plaid re-auth banner state — populated from /plaid/connection-health
  const [reauthNeeded, setReauthNeeded] = useState([]);
  const [reauthLoading, setReauthLoading] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showConnectBank, setShowConnectBank] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [syncingTransactions, setSyncingTransactions] = useState(false);
  const [formData, setFormData] = useState({});
  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [show2FAConfirm, setShow2FAConfirm] = useState(false);
  const [twoFATogglePassword, setTwoFATogglePassword] = useState('');
  const [linkToken, setLinkToken] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [accountantEmail, setAccountantEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  // httpOnly cookie auth — session check via /auth/me
  // Only cache display-safe fields (no tokens or sensitive data)
  const cacheUser = (userData) => {
    const { name, email, is_admin, subscription_status, is_demo_prospect, two_factor_enabled, demo_expires_at } = userData || {};
    localStorage.setItem('mybookie_user', JSON.stringify({ name, email, is_admin, subscription_status, is_demo_prospect, two_factor_enabled, demo_expires_at }));
  };
  const [token, setToken] = useState(null); // true-ish when authenticated
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mybookie_user') || '{}'); } catch (err) { console.error("Parse stored user:", err); return {}; }
  });
  const [authChecked, setAuthChecked] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user.two_factor_enabled || false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [twoFANudgeDismissed, setTwoFANudgeDismissed] = useState(() =>
    localStorage.getItem('mybookie_2fa_nudge_dismissed') === '1'
  );
  const dismissTwoFANudge = () => {
    localStorage.setItem('mybookie_2fa_nudge_dismissed', '1');
    setTwoFANudgeDismissed(true);
  };

  // Demo prospect detection
  const isDemoProspect = user.is_demo_prospect || false;
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertName, setConvertName] = useState('');
  const [convertPassword, setConvertPassword] = useState('');
  const [convertLoading, setConvertLoading] = useState(false);

  // Paid tiers that can use Plaid bank connections
  const isPaidUser = !isDemoProspect && (user.is_admin || ['pro', 'business', 'business_plus'].includes(user.subscription_status));
  const [emailVerified, setEmailVerified] = useState(user.email_verified || false);
  const [verifyCode, setVerifyCode] = useState('');
  const [showVerifyBanner, setShowVerifyBanner] = useState(!user.email_verified && !user.is_admin);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifySent, setVerifySent] = useState(false);

  // Auth: httpOnly cookies are primary, sessionStorage token is fallback for cross-origin deployments
  const storedToken = sessionStorage.getItem('mybookie_token');
  const authHeaders = storedToken ? { headers: { Authorization: `Bearer ${storedToken}` } } : {};

  // Poll Stripe checkout status after redirect from payment
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const upgrade = searchParams.get('upgrade');
    const backontrack = searchParams.get('backontrack');
    
    if (sessionId && upgrade === 'success') {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const response = await axios.get(
            `${API}/api/mybookie/stripe/checkout-status/${sessionId}`,
            authHeaders
          );
          if (response.data.payment_status === 'paid') {
            // Update local user data with new tier
            const updatedUser = { ...user, subscription_status: response.data.tier };
            cacheUser(updatedUser);
            setUser(updatedUser);
            toast({ title: "Upgrade Successful!", description: `You're now on the ${response.data.plan_name} plan.` });
            // Clean URL
            setSearchParams({});
            // Reload to reflect new tier
            window.location.href = isMyBookieDomain() ? '/dashboard' : '/mybookie/dashboard';
          } else {
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } catch (err) {
          console.error("Stripe poll error:", err);
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      };
      pollStatus();
    }
    
    // Handle Back on Track checkout success
    if (sessionId && backontrack === 'success') {
      const pollBackOnTrack = async (attempts = 0) => {
        if (attempts >= 5) {
          toast({ title: "Payment Received", description: "Your Back on Track order is being processed. Upload your bank statements now." });
          setSearchParams({});
          setActiveTab('backontrack');
          return;
        }
        try {
          const response = await axios.get(
            `${API}/api/mybookie/stripe/checkout-status/${sessionId}`,
            authHeaders
          );
          if (response.data.payment_status === 'paid') {
            toast({ title: "Payment Successful!", description: "Your Back on Track order is ready. Upload your bank statements now." });
            setSearchParams({});
            setActiveTab('backontrack');
          } else {
            setTimeout(() => pollBackOnTrack(attempts + 1), 2000);
          }
        } catch (err) {
          console.error("BackOnTrack poll error:", err);
          setTimeout(() => pollBackOnTrack(attempts + 1), 2000);
        }
      };
      pollBackOnTrack();
    }
  }, []); // Run once on mount

  // Convert demo account handler
  const handleConvertDemo = async (e) => {
    e.preventDefault();
    if (convertPassword.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    setConvertLoading(true);
    try {
      const response = await axios.post(`${API}/api/mybookie/auth/convert-demo`, {
        name: convertName,
        password: convertPassword
      }, authHeaders);
      // Cookie is set automatically by backend
      cacheUser(response.data.user);
      toast({ title: "Account Upgraded!", description: "Welcome to MyBookie! Your data has been preserved." });
      setShowConvertDialog(false);
      window.location.reload();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Could not convert account", variant: "destructive" });
    } finally {
      setConvertLoading(false);
    }
  };

  // Email verification handlers
  const handleSendVerification = async () => {
    setVerifyLoading(true);
    try {
      const res = await axios.post(`${API}/api/mybookie/auth/send-verification`, {}, authHeaders);
      setVerifySent(true);
      toast({ title: "Verification code sent", description: "Check your email for the code" });
    } catch (err) {
      console.error("Send verification error:", err);
      toast({ title: "Error", description: "Failed to send verification", variant: "destructive" });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!verifyCode.trim()) return;
    setVerifyLoading(true);
    try {
      await axios.post(`${API}/api/mybookie/auth/verify-email?code=${verifyCode}`, {}, authHeaders);
      setEmailVerified(true);
      setShowVerifyBanner(false);
      const updatedUser = { ...user, email_verified: true };
      cacheUser(updatedUser);
      setUser(updatedUser);
      toast({ title: "Email Verified!", description: "Your email has been verified" });
    } catch (err) {
      console.error("Verify code error:", err);
      toast({ title: "Error", description: "Invalid or expired code", variant: "destructive" });
    } finally {
      setVerifyLoading(false);
    }
  };

  // Export to CSV helper function
  const exportToCSV = (data, filename, headers) => {
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => {
        const val = row[h.toLowerCase().replace(/ /g, '_')] || row[h] || '';
        // Escape quotes and wrap in quotes if contains comma
        const strVal = String(val);
        if (strVal.includes(',') || strVal.includes('"')) {
          return `"${strVal.replace(/"/g, '""')}"`;
        }
        return strVal;
      }).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  // Export expenses to CSV
  const exportExpensesCSV = () => {
    if (!expenses.length) {
      toast({ title: "No Data", description: "No expenses to export", variant: "destructive" });
      return;
    }
    const data = expenses.map(e => ({
      date: e.date,
      category: e.category_name,
      description: e.description,
      vendor: e.vendor || '',
      amount: e.amount.toFixed(2)
    }));
    exportToCSV(data, `${selectedBusiness.name}_expenses_${selectedYear}.csv`, ['Date', 'Category', 'Description', 'Vendor', 'Amount']);
    toast({ title: "Downloaded", description: "Expenses exported to CSV" });
  };

  // Export income to CSV
  const exportIncomeCSV = () => {
    if (!income.length) {
      toast({ title: "No Data", description: "No income to export", variant: "destructive" });
      return;
    }
    const data = income.map(i => ({
      date: i.date,
      category: i.category_name,
      description: i.description,
      source: i.source || '',
      amount: i.amount.toFixed(2)
    }));
    exportToCSV(data, `${selectedBusiness.name}_income_${selectedYear}.csv`, ['Date', 'Category', 'Description', 'Source', 'Amount']);
    toast({ title: "Downloaded", description: "Income exported to CSV" });
  };

  // Export P&L report to CSV
  const exportPnLCSV = () => {
    if (!pnlReport) {
      toast({ title: "No Data", description: "No report to export", variant: "destructive" });
      return;
    }
    
    const rows = [];
    // Header
    rows.push({ section: 'PROFIT & LOSS STATEMENT', category: '', amount: '' });
    rows.push({ section: `Business: ${selectedBusiness.name}`, category: '', amount: '' });
    rows.push({ section: `Year: ${selectedYear}`, category: '', amount: '' });
    rows.push({ section: '', category: '', amount: '' });
    
    // Income section
    rows.push({ section: 'INCOME', category: '', amount: '' });
    Object.entries(pnlReport.income_by_category || {}).forEach(([cat, data]) => {
      rows.push({ section: '', category: cat, amount: data.total.toFixed(2) });
    });
    rows.push({ section: '', category: 'Total Income', amount: pnlReport.summary?.total_income?.toFixed(2) || '0.00' });
    rows.push({ section: '', category: '', amount: '' });
    
    // Expense section
    rows.push({ section: 'EXPENSES', category: '', amount: '' });
    Object.entries(pnlReport.expense_by_category || {}).forEach(([cat, data]) => {
      rows.push({ section: '', category: cat, amount: data.total.toFixed(2) });
    });
    rows.push({ section: '', category: 'Total Expenses', amount: pnlReport.summary?.total_expenses?.toFixed(2) || '0.00' });
    rows.push({ section: '', category: '', amount: '' });
    
    // Net profit
    rows.push({ section: 'NET PROFIT/(LOSS)', category: '', amount: pnlReport.summary?.net_profit?.toFixed(2) || '0.00' });
    rows.push({ section: '', category: '', amount: '' });
    
    // Monthly breakdown
    rows.push({ section: 'MONTHLY BREAKDOWN', category: '', amount: '' });
    rows.push({ section: 'Month', category: 'Income', amount: 'Expenses,Net' });
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    Object.entries(pnlReport.monthly_breakdown || {}).forEach(([month, data]) => {
      rows.push({ 
        section: monthNames[parseInt(month) - 1], 
        category: data.income.toFixed(2), 
        amount: `${data.expenses.toFixed(2)},${data.net.toFixed(2)}` 
      });
    });
    
    exportToCSV(rows, `${selectedBusiness.name}_PnL_${selectedYear}.csv`, ['Section', 'Category', 'Amount']);
    toast({ title: "Downloaded", description: "P&L report exported to CSV" });
  };

  // Generate shareable summary text
  const generateShareableSummary = () => {
    if (!pnlReport || !selectedBusiness) return '';
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    let summary = `📊 FINANCIAL SUMMARY - ${selectedBusiness.name}\n`;
    summary += `Tax Year: ${selectedYear}\n`;
    summary += `Generated: ${new Date().toLocaleDateString()}\n\n`;
    
    summary += `═══════════════════════════════\n`;
    summary += `SUMMARY\n`;
    summary += `═══════════════════════════════\n`;
    summary += `Total Income:   ${formatCurrency(pnlReport.summary?.total_income)}\n`;
    summary += `Total Expenses: ${formatCurrency(pnlReport.summary?.total_expenses)}\n`;
    summary += `Net Profit:     ${formatCurrency(pnlReport.summary?.net_profit)}\n`;
    summary += `Profit Margin:  ${(pnlReport.summary?.profit_margin || 0).toFixed(1)}%\n\n`;
    
    if (Object.keys(pnlReport.income_by_category || {}).length > 0) {
      summary += `═══════════════════════════════\n`;
      summary += `INCOME BY CATEGORY\n`;
      summary += `═══════════════════════════════\n`;
      Object.entries(pnlReport.income_by_category).forEach(([cat, data]) => {
        summary += `${cat}: ${formatCurrency(data.total)}\n`;
      });
      summary += `\n`;
    }
    
    if (Object.keys(pnlReport.expense_by_category || {}).length > 0) {
      summary += `═══════════════════════════════\n`;
      summary += `EXPENSES BY CATEGORY\n`;
      summary += `═══════════════════════════════\n`;
      Object.entries(pnlReport.expense_by_category).forEach(([cat, data]) => {
        summary += `${cat}: ${formatCurrency(data.total)}\n`;
      });
    }
    
    return summary;
  };

  // Copy summary to clipboard
  const copyToClipboard = async () => {
    const summary = generateShareableSummary();
    await navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Copied!", description: "Summary copied to clipboard" });
  };

  // Email summary to accountant
  const emailToAccountant = () => {
    const summary = generateShareableSummary();
    const subject = encodeURIComponent(`Financial Summary - ${selectedBusiness?.name} - ${selectedYear}`);
    const body = encodeURIComponent(summary);
    window.open(`mailto:${accountantEmail}?subject=${subject}&body=${body}`);
    toast({ title: "Email Opened", description: "Your email client should open with the summary" });
    setShowShareDialog(false);
  };

  // Finance Bot message handler (returns response text for FinanceBotWidget)
  const handleBotMessage = async (message) => {
    const response = await axios.post(`${API}/api/mybookie/chat`, {
      message,
      business_id: selectedBusiness?.id || null
    }, authHeaders);
    return response.data.response;
  };

  // Share link generation handler (for ShareDialog)
  const handleGenerateShareLink = async () => {
    if (!selectedBusiness) return;
    try {
      const res = await axios.post(`${API}/api/mybookie/accountant/invite`, {
        business_id: selectedBusiness.id,
        email: accountantEmail
      }, authHeaders);
      const shareUrl = `${window.location.origin}/shared/${res.data.share_token}`;
      navigator.clipboard.writeText(shareUrl);
      toast({ title: "Share link created & copied!", description: shareUrl });
    } catch (err) {
      console.error("Share link error:", err);
      toast({ title: "Error", description: "Failed to create share link", variant: "destructive" });
    }
  };

  // Upgrade action handler (for UpgradeDialog)
  const handleUpgradeAction = async () => {
    if (isDemoProspect) {
      setShowUpgradeDialog(false);
      setShowConvertDialog(true);
    } else {
      setShowUpgradeDialog(false);
      try {
        const response = await axios.post(
          `${API}/api/mybookie/stripe/create-checkout`,
          { package_id: 'pro', origin_url: window.location.origin },
          authHeaders
        );
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      } catch (err) {
        console.error("Stripe checkout error:", err);
        navigate(isMyBookieDomain() ? '/pricing' : '/mybookie/pricing');
      }
    }
  };

  // Plaid Link success callback
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    try {
      await axios.post(`${API}/api/mybookie/plaid/exchange-token`, null, {
        ...authHeaders,
        params: {
          public_token: publicToken,
          business_id: selectedBusiness.id,
          institution_name: metadata.institution?.name,
          link_session_id: metadata.link_session_id || null
        }
      });
      
      toast({ title: "Success!", description: `${metadata.institution?.name || 'Bank'} connected successfully` });
      fetchConnectedAccounts(selectedBusiness.id);
      setShowConnectBank(false);
      setLinkToken(null);
      
      // Auto-sync after connection
      handleSyncTransactions();
    } catch (error) {
      toast({ 
        title: "Connection Failed", 
        description: error.response?.data?.detail || "Could not connect bank account",
        variant: "destructive"
      });
    }
  }, [selectedBusiness, authHeaders, toast]);

  // Plaid Link exit callback
  const onPlaidExit = useCallback((err, metadata) => {
    if (err) {
      console.error('[Plaid Link] Exit error:', err, 'link_session_id:', metadata?.link_session_id);
    }
    setShowConnectBank(false);
    setLinkToken(null);
  }, []);

  // Plaid Link handler ref (direct CDN integration — bypasses react-plaid-link bugs)
  const plaidHandlerRef = useRef(null);

  // Load Plaid CDN script once
  useEffect(() => {
    if (document.getElementById('plaid-link-script')) return;
    const script = document.createElement('script');
    script.id = 'plaid-link-script';
    script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  // Get Plaid Link token and open Link directly via CDN
  const initializePlaidLink = async () => {
    if (!selectedBusiness) {
      toast({ title: "Select a business first", variant: "destructive" });
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/mybookie/plaid/create-link-token`, null, {
        ...authHeaders,
        params: { business_id: selectedBusiness.id }
      });
      
      const token = response.data.link_token;
      console.log('[Plaid] Token received:', token?.substring(0, 35) + '...');
      console.log('[Plaid] Environment:', token?.startsWith('link-production') ? 'PRODUCTION' : 'SANDBOX');
      setLinkToken(token);

      // Wait for Plaid CDN script to be available
      const waitForPlaid = () => new Promise((resolve) => {
        if (window.Plaid) return resolve();
        const check = setInterval(() => {
          if (window.Plaid) { clearInterval(check); resolve(); }
        }, 100);
        setTimeout(() => { clearInterval(check); resolve(); }, 5000);
      });
      
      await waitForPlaid();

      if (!window.Plaid) {
        toast({ title: "Error", description: "Plaid failed to load. Please refresh and try again.", variant: "destructive" });
        setShowConnectBank(false);
        return;
      }

      // Destroy any previous handler
      if (plaidHandlerRef.current) {
        try { plaidHandlerRef.current.destroy(); } catch (e) { /* ignore */ }
      }

      // Create fresh Plaid Link handler with production token
      const handler = window.Plaid.create({
        token: token,
        onSuccess: (publicToken, metadata) => {
          console.log('[Plaid] Success! Institution:', metadata?.institution?.name);
          onPlaidSuccess(publicToken, metadata);
        },
        onExit: (err, metadata) => {
          console.log('[Plaid] Exit. Error:', err);
          onPlaidExit(err, metadata);
        },
        onLoad: () => {
          console.log('[Plaid] Link loaded, opening...');
        },
      });

      plaidHandlerRef.current = handler;
      handler.open();
      
    } catch (error) {
      console.error('[Plaid] Init error:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Could not initialize bank connection",
        variant: "destructive"
      });
      setShowConnectBank(false);
    }
  };

  // Session check — tries cookie first, falls back to sessionStorage token
  useEffect(() => {
    const checkSession = async () => {
      try {
        const tokenHeader = sessionStorage.getItem('mybookie_token');
        const headers = tokenHeader ? { Authorization: `Bearer ${tokenHeader}` } : {};
        const res = await axios.get(`${API}/api/mybookie/auth/me`, { headers });
        setUser(res.data);
        setToken(tokenHeader || 'authenticated');
        cacheUser(res.data);
        setTwoFactorEnabled(res.data.two_factor_enabled || false);
      } catch (err) {
        console.error("Session check failed:", err);
        // No valid session — redirect to landing with error hint if user just tried to log in
        const hadUser = localStorage.getItem('mybookie_user');
        localStorage.removeItem('mybookie_user');
        if (hadUser) {
          // User had a login attempt but session cookie wasn't stored (likely CORS issue)
          navigate(getLandingPath() + '?auth=expired');
        } else {
          navigate(getLandingPath());
        }
      } finally {
        setAuthChecked(true);
      }
    };
    checkSession();
  }, [navigate]);

  // Fetch businesses - Use MyBookie API
  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/api/mybookie/businesses`, authHeaders);
      setBusinesses(response.data.businesses || []);
      if (response.data.businesses?.length > 0 && !selectedBusiness) {
        setSelectedBusiness(response.data.businesses[0]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate(getLandingPath());
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async (businessId) => {
    try {
      const response = await axios.get(`${API}/api/mybookie/categories/${businessId}`, authHeaders);
      setCategories(response.data);
    } catch (error) {
    }
  };

  // Fetch expenses
  const fetchExpenses = async (businessId) => {
    try {
      const params = new URLSearchParams();
      params.append('year', selectedYear);
      if (selectedMonth) params.append('month', selectedMonth);
      
      const response = await axios.get(`${API}/api/mybookie/expenses/${businessId}?${params.toString()}`, authHeaders);
      setExpenses(response.data.expenses || []);
    } catch (error) {
    }
  };

  // Fetch income
  const fetchIncome = async (businessId) => {
    try {
      const params = new URLSearchParams();
      params.append('year', selectedYear);
      if (selectedMonth) params.append('month', selectedMonth);
      
      const response = await axios.get(`${API}/api/mybookie/income/${businessId}?${params.toString()}`, authHeaders);
      setIncome(response.data.income || []);
    } catch (error) {
    }
  };

  // Fetch P&L report
  const fetchPnLReport = async (businessId) => {
    try {
      const response = await axios.get(`${API}/api/mybookie/reports/pnl/${businessId}?year=${selectedYear}&base_currency=${baseCurrency}`, authHeaders);
      setPnlReport(response.data);
    } catch (error) {
    }
  };

  // Fetch cash flow forecast
  const fetchCashflowForecast = async (businessId) => {
    try {
      const response = await axios.get(`${API}/api/mybookie/reports/cashflow/${businessId}?months=6`, authHeaders);
      setCashflowForecast(response.data);
    } catch (error) {
      setCashflowForecast(null);
    }
  };

  // Fetch recurring items
  const fetchRecurringItems = async (businessId) => {
    try {
      const response = await axios.get(`${API}/api/mybookie/recurring/${businessId}`, authHeaders);
      setRecurringItems(response.data);
    } catch (error) {
      setRecurringItems({ recurring_expenses: [], recurring_income: [] });
    }
  };

  // Fetch connected bank accounts
  const fetchConnectedAccounts = async (businessId) => {
    try {
      const response = await axios.get(`${API}/api/mybookie/plaid/accounts/${businessId}`, authHeaders);
      setConnectedAccounts(response.data.accounts || []);
    } catch (error) {
      setConnectedAccounts([]);
    }
  };

  // Disconnect bank account
  const handleDisconnectBank = async () => {
    if (!selectedBusiness || connectedAccounts.length === 0) return;
    if (!window.confirm('Are you sure you want to disconnect your bank? This will remove the connection but keep existing transactions.')) return;
    try {
      const accountId = connectedAccounts[0].account_id;
      await axios.delete(`${API}/api/mybookie/plaid/disconnect/${selectedBusiness.id}/${accountId}`, authHeaders);
      setConnectedAccounts([]);
      toast({ title: "Bank disconnected", description: "Your bank account has been disconnected successfully." });
    } catch (error) {
      toast({ title: "Error", description: "Failed to disconnect bank", variant: "destructive" });
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      fetchCategories(selectedBusiness.id);
      fetchExpenses(selectedBusiness.id);
      fetchIncome(selectedBusiness.id);
      fetchPnLReport(selectedBusiness.id);
      fetchConnectedAccounts(selectedBusiness.id);
      fetchCashflowForecast(selectedBusiness.id);
      fetchRecurringItems(selectedBusiness.id);
    }
  }, [selectedBusiness, selectedYear, selectedMonth, baseCurrency]);

  // Create business
  const handleCreateBusiness = async () => {
    try {
      await axios.post(`${API}/api/mybookie/businesses`, formData, authHeaders);
      toast({ title: "Success", description: "Business created successfully" });
      setShowAddBusiness(false);
      setFormData({});
      fetchBusinesses();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to create business", variant: "destructive" });
    }
  };

  // Create expense
  const handleCreateExpense = async () => {
    try {
      await axios.post(`${API}/api/mybookie/expenses`, {
        ...formData,
        business_id: selectedBusiness.id
      }, authHeaders);
      toast({ title: "Success", description: "Expense added successfully" });
      setShowAddExpense(false);
      setFormData({});
      fetchExpenses(selectedBusiness.id);
      fetchPnLReport(selectedBusiness.id);
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to add expense", variant: "destructive" });
    }
  };

  // Create income
  const handleCreateIncome = async () => {
    try {
      await axios.post(`${API}/api/mybookie/income`, {
        ...formData,
        business_id: selectedBusiness.id
      }, authHeaders);
      toast({ title: "Success", description: "Income added successfully" });
      setShowAddIncome(false);
      setFormData({});
      fetchIncome(selectedBusiness.id);
      fetchPnLReport(selectedBusiness.id);
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to add income", variant: "destructive" });
    }
  };

  // Delete expense
  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/api/mybookie/expenses/${expenseId}`, authHeaders);
      toast({ title: "Deleted", description: "Expense removed" });
      fetchExpenses(selectedBusiness.id);
      fetchPnLReport(selectedBusiness.id);
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete expense", variant: "destructive" });
    }
  };

  // Delete income
  const handleDeleteIncome = async (incomeId) => {
    if (!window.confirm('Delete this income entry?')) return;
    try {
      await axios.delete(`${API}/api/mybookie/income/${incomeId}`, authHeaders);
      toast({ title: "Deleted", description: "Income removed" });
      fetchIncome(selectedBusiness.id);
      fetchPnLReport(selectedBusiness.id);
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete income", variant: "destructive" });
    }
  };

  // Rename / edit business (name + notification email override)
  const handleRenameBusiness = async () => {
    if (!selectedBusiness || !editBusinessName.trim()) return;
    try {
      // Empty email string is sent as "" — backend interprets that as "clear override"
      const payload = {
        name: editBusinessName.trim(),
        notification_email: editBusinessNotifyEmail.trim(),
      };
      await axios.put(`${API}/api/mybookie/businesses/${selectedBusiness.id}`, payload, authHeaders);
      toast({ title: "Updated", description: "Business settings saved" });
      setShowEditBusiness(false);
      setSelectedBusiness({
        ...selectedBusiness,
        name: editBusinessName.trim(),
        notification_email: editBusinessNotifyEmail.trim() || null,
      });
      fetchBusinesses();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update business", variant: "destructive" });
    }
  };

  // Manually trigger the weekly Finance Brief email for the selected business
  const handleSendBriefNow = async () => {
    if (!selectedBusiness) return;
    setSendingBrief(true);
    try {
      const res = await axios.post(
        `${API}/api/mybookie/cfo/finance-brief/${selectedBusiness.id}/send-now`,
        {}, authHeaders
      );
      toast({
        title: "Brief sent",
        description: `Sent to ${res.data.to}`,
      });
    } catch (error) {
      toast({
        title: "Couldn't send brief",
        description: error.response?.data?.detail || "Try again in a moment",
        variant: "destructive",
      });
    } finally {
      setSendingBrief(false);
    }
  };

  // Delete business
  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm('Delete this business and ALL its data? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/mybookie/businesses/${businessId}`, authHeaders);
      toast({ title: "Deleted", description: "Business removed" });
      setSelectedBusiness(null);
      fetchBusinesses();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete business", variant: "destructive" });
    }
  };

  // Edit expense/income
  const [editingItem, setEditingItem] = useState(null);
  const [editType, setEditType] = useState(null); // 'expense' or 'income'
  const [editForm, setEditForm] = useState({});

  const openEditDialog = (item, type) => {
    setEditingItem(item);
    setEditType(type);
    setEditForm({
      amount: item.amount,
      description: item.description,
      date: item.date,
      category_id: item.category_id || '',
      ...(type === 'expense' ? { vendor: item.vendor || '' } : { source: item.source || '' }),
      notes: item.notes || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingItem || !editType) return;
    const endpoint = editType === 'expense' 
      ? `${API}/api/mybookie/expenses/${editingItem.id}`
      : `${API}/api/mybookie/income/${editingItem.id}`;
    try {
      await axios.put(endpoint, editForm, authHeaders);
      toast({ title: "Updated", description: `${editType === 'expense' ? 'Expense' : 'Income'} updated` });
      setEditingItem(null);
      if (editType === 'expense') {
        fetchExpenses(selectedBusiness.id);
      } else {
        fetchIncome(selectedBusiness.id);
      }
      fetchPnLReport(selectedBusiness.id);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  // Bulk recategorize
  const handleBulkRecategorize = async (expenseIds, categoryId) => {
    if (!expenseIds?.length || !categoryId) return;
    try {
      const response = await axios.post(
        `${API}/api/mybookie/expenses/bulk-recategorize`,
        { expense_ids: expenseIds, category_id: categoryId, learn: true },
        authHeaders
      );
      const { updated_count = 0, vendors_learned = 0 } = response.data || {};
      toast({
        title: "Bulk Updated",
        description: `${updated_count} expense${updated_count === 1 ? '' : 's'} re-categorized${vendors_learned > 0 ? ` • ${vendors_learned} vendor${vendors_learned === 1 ? '' : 's'} learned for future syncs` : ''}`,
      });
      if (selectedBusiness) {
        fetchExpenses(selectedBusiness.id);
        fetchPnLReport(selectedBusiness.id);
      }
    } catch (error) {
      toast({
        title: "Bulk Update Failed",
        description: error.response?.data?.detail || "Could not re-categorize. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Connect bank (Plaid Link) - PAID TIERS ONLY
  const handleConnectBank = async () => {
    if (!isPaidUser) {
      setShowUpgradeDialog(true);
      return;
    }
    if (!selectedBusiness) {
      toast({ title: "Select a business first", variant: "destructive" });
      return;
    }
    
    setShowConnectBank(true);
    await initializePlaidLink();
  };

  // Fetch which Plaid Items need re-authentication (populated from webhooks server-side)
  const fetchReauthNeeded = async () => {
    try {
      const res = await axios.get(`${API}/api/mybookie/plaid/connection-health`, authHeaders);
      const flagged = (res.data.items || []).filter(i => i.needs_reauth);
      setReauthNeeded(flagged);
    } catch (e) {
      // Non-fatal — free/demo users can't hit this
    }
  };

  useEffect(() => {
    if (isPaidUser) fetchReauthNeeded();
  }, [isPaidUser]);

  // Trigger Plaid Link in UPDATE mode to re-authenticate a specific Item.
  // On success, backend clears needs_reauth and the next daily sync backfills everything.
  const handleReconnectBank = async (itemId) => {
    setReauthLoading(true);
    try {
      // 1. Ensure Plaid Link SDK is loaded (already loaded on initial dashboard mount)
      await new Promise((resolve) => {
        if (window.Plaid) return resolve();
        const check = setInterval(() => {
          if (window.Plaid) { clearInterval(check); resolve(); }
        }, 100);
        setTimeout(() => { clearInterval(check); resolve(); }, 5000);
      });
      if (!window.Plaid) {
        toast({ title: "Bank widget unavailable", description: "Please refresh and try again.", variant: "destructive" });
        return;
      }
      // 2. Get an update-mode link token for this item
      const res = await axios.post(`${API}/api/mybookie/plaid/link-token/update/${itemId}`, {}, authHeaders);
      const linkToken = res.data.link_token;
      // 3. Open Plaid Link in update mode
      const handler = window.Plaid.create({
        token: linkToken,
        onSuccess: async () => {
          try {
            await axios.post(`${API}/api/mybookie/plaid/reauth-complete/${itemId}`, {}, authHeaders);
            toast({ title: "Reconnected", description: "Syncing transactions since your last update…" });
            await fetchReauthNeeded();
            // Trigger a fresh sync so April→today backfills automatically
            if (selectedBusiness?.id) handleSyncTransactions(true);
          } catch (_e) {
            // Non-fatal — sync will retry on next daily cycle
          }
        },
        onExit: (err) => {
          if (err) toast({ title: "Reconnect cancelled", description: "You can retry anytime." });
        },
      });
      handler.open();
    } catch (e) {
      toast({ title: "Reconnect failed", description: e.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setReauthLoading(false);
    }
  };

  // Sync transactions - PAID TIERS ONLY
  const handleSyncTransactions = async (forceSync = false) => {
    if (!isPaidUser) {
      setShowUpgradeDialog(true);
      return;
    }
    setSyncingTransactions(true);
    try {
      const url = forceSync 
        ? `${API}/api/mybookie/plaid/sync/${selectedBusiness.id}?force=true`
        : `${API}/api/mybookie/plaid/sync/${selectedBusiness.id}`;
      const response = await axios.post(url, {}, authHeaders);
      if (response.data.cooldown) {
        const mins = Math.ceil((response.data.next_sync_in || 900) / 60);
        toast({ title: "Recently Synced", description: `Next sync available in ${mins} minutes.` });
      } else if (response.data.plaid_preparing) {
        toast({ title: "Preparing Data", description: "Plaid is still preparing your transaction history. This can take up to 30 minutes after connecting a new bank. Try again shortly." });
      } else {
        const count = response.data.transactions_added || 0;
        toast({ title: count > 0 ? "Transactions Imported!" : "Synced", description: count > 0 ? `${count} transactions imported (${response.data.auto_categorized || 0} auto-categorized)` : "No new transactions found. If you just connected, Plaid may need 5-30 minutes to prepare your history." });
        if (count > 0) {
          fetchExpenses(selectedBusiness.id);
          fetchIncome(selectedBusiness.id);
          fetchPnLReport(selectedBusiness.id);
        }
      }
    } catch (error) {
      console.error("[Plaid Sync] Error:", error.response?.data || error);
      toast({ 
        title: "Sync Error", 
        description: error.response?.data?.detail || "Failed to sync. Please try again." ,
        variant: "destructive"
      });
    } finally {
      setSyncingTransactions(false);
    }
  };

  // Logout - clear httpOnly cookie via backend + clear local UI state
  const handleLogout = async () => {
    try { await axios.post(`${API}/api/mybookie/auth/logout`, {}, authHeaders); } catch (err) { console.error("Logout:", err); }
    localStorage.removeItem('mybookie_user');
    sessionStorage.removeItem('mybookie_token');
    setToken(null);
    navigate(getLandingPath());
  };

  // Profile handlers
  const handleUpdateProfile = async () => {
    if (!profileForm.name.trim()) return;
    setProfileLoading(true);
    try {
      const response = await axios.put(`${API}/api/mybookie/auth/profile`, { name: profileForm.name.trim() }, authHeaders);
      const updatedUser = response.data.user;
      cacheUser(updatedUser);
      setUser(updatedUser);
      toast({ title: "Profile updated", description: "Your name has been updated" });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to update profile", variant: "destructive" });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast({ title: "Error", description: "New passwords don't match", variant: "destructive" });
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    setProfileLoading(true);
    try {
      await axios.put(`${API}/api/mybookie/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, authHeaders);
      toast({ title: "Password changed", description: "Your password has been updated" });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to change password", variant: "destructive" });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleToggle2FA = async () => {
    if (!twoFATogglePassword) {
      toast({ title: "Error", description: "Please enter your password to confirm", variant: "destructive" });
      return;
    }
    setProfileLoading(true);
    try {
      const newState = !twoFactorEnabled;
      await axios.post(`${API}/api/mybookie/auth/toggle-2fa`, {
        password: twoFATogglePassword,
        enable: newState
      }, authHeaders);
      setTwoFactorEnabled(newState);
      setShow2FAConfirm(false);
      setTwoFATogglePassword('');
      const updatedUser = { ...user, two_factor_enabled: newState };
      cacheUser(updatedUser);
      setUser(updatedUser);
      toast({ title: newState ? "2FA Enabled" : "2FA Disabled", description: newState ? "You'll receive a code via email on each login" : "Two-factor authentication has been turned off" });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to update 2FA", variant: "destructive" });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleDeleteAccountFn = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This will permanently remove ALL your businesses, transactions, and data. This cannot be undone.')) return;
    if (!window.confirm('This is your final warning. Type OK below to confirm.')) return;
    try {
      await axios.delete(`${API}/api/mybookie/auth/account`, authHeaders);
      toast({ title: "Account deleted", description: "Your account has been permanently deleted" });
      try { await axios.post(`${API}/api/mybookie/auth/logout`, {}, authHeaders); } catch (err) { console.error("Logout on delete:", err); }
      localStorage.removeItem('mybookie_user');
      sessionStorage.removeItem('mybookie_token');
      navigate(getLandingPath());
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete account", variant: "destructive" });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const businessTypes = [
    "Tech Platform",
    "Real Estate",
    "E-Commerce",
    "Consulting",
    "Freelance",
    "General"
  ];

  const months = [
    { value: null, label: 'All Months' },
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  if (!authChecked || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2B1D]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E8E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#0A2B1D] rounded-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-[#111614]">MyBookie</span>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                setProfileForm({ name: user.name || '' });
                setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                setShowProfile(true);
              }}
              className="text-sm text-[#5C6661] hover:text-[#111614] hidden sm:flex items-center gap-1 transition-colors"
              data-testid="profile-link"
            >
              <User className="w-3.5 h-3.5" />
              {user.email}
            </button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#5C6661] hover:text-[#111614] hover:bg-[#E2ECE7]" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Email Verification Banner */}
      {showVerifyBanner && (
        <div className="bg-[#FFF8E1] border-b border-[#FFE082] px-4 py-3" data-testid="verify-email-banner">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-sm text-[#5D4037]">
              Please verify your email address to unlock all features.
            </p>
            <div className="flex items-center gap-2">
              {!verifySent ? (
                <Button size="sm" className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm text-xs h-7" onClick={handleSendVerification} disabled={verifyLoading} data-testid="send-verify-btn">
                  {verifyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Send Code'}
                </Button>
              ) : (
                <>
                  <Input 
                    placeholder="Enter code" 
                    value={verifyCode} 
                    onChange={(e) => setVerifyCode(e.target.value)} 
                    className="h-7 w-32 text-xs" 
                    data-testid="verify-code-input"
                  />
                  <Button size="sm" className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm text-xs h-7" onClick={handleVerifyEmail} disabled={verifyLoading} data-testid="verify-submit-btn">
                    {verifyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify'}
                  </Button>
                </>
              )}
              <button onClick={() => setShowVerifyBanner(false)} className="text-[#8A948F] hover:text-[#5D4037] text-xs ml-2">Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {/* Demo Prospect Banner */}
      {isDemoProspect && (
        <div className="bg-gradient-to-r from-[#0A2B1D] to-[#1A3D2E] border-b border-[#3A6E53] px-4 py-3" data-testid="demo-prospect-banner">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                <Clock className="w-3.5 h-3.5 text-[#9CC4AF]" />
              </div>
              <p className="text-sm text-[#B4C9BE]">
                <span className="font-medium text-white">Demo Account</span> — 
                {user.demo_expires_at ? ` Expires ${new Date(user.demo_expires_at).toLocaleDateString()}` : ' 30 days remaining'}. 
                Upgrade to unlock bank connections and keep your data.
              </p>
            </div>
            <Button 
              size="sm" 
              className="bg-white text-[#0A2B1D] hover:bg-[#E2ECE7] rounded-sm text-xs h-7 font-medium"
              onClick={() => setShowConvertDialog(true)}
              data-testid="convert-demo-btn"
            >
              <Crown className="w-3 h-3 mr-1" /> Upgrade Account
            </Button>
          </div>
        </div>
      )}

      {/* Bank Connection Re-auth Banner — shown when Plaid webhook flagged an item */}
      {reauthNeeded && reauthNeeded.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3" data-testid="reauth-banner">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4 h-4 text-amber-700" />
              </div>
              <p className="text-sm text-amber-900 leading-snug">
                <span className="font-medium">
                  {reauthNeeded.length === 1
                    ? `${reauthNeeded[0].business_name || 'Your business'}'s bank connection needs a quick refresh`
                    : `${reauthNeeded.length} bank connections need a quick refresh`}
                </span>{' '}
                — banks require re-verification every 60–90 days. Takes ~30 seconds.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => handleReconnectBank(reauthNeeded[0].item_id)}
              disabled={reauthLoading}
              className="bg-amber-700 hover:bg-amber-800 text-white rounded-sm text-xs h-7 font-medium"
              data-testid="reauth-btn"
            >
              {reauthLoading ? 'Opening…' : (<>Reconnect <RefreshCw className="w-3 h-3 ml-1" /></>)}
            </Button>
          </div>
        </div>
      )}

      {/* 2FA Nudge Banner — appears for non-demo users who haven't enabled 2FA */}
      {!isDemoProspect && !twoFactorEnabled && !twoFANudgeDismissed && (
        <div className="bg-[#FFF7E6] border-b border-[#F0DCA8] px-4 py-3" data-testid="twofa-nudge-banner">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-[#B4881F]/15 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-[#8A6510]" />
              </div>
              <p className="text-sm text-[#5C4810] leading-snug">
                <span className="font-medium text-[#3D2F08]">
                  {user.is_admin
                    ? "You're one setting away from an A+ security audit."
                    : "Add a second layer of protection to your books."}
                </span>{' '}
                Enable 2FA in 60 seconds — it locks your account even if your password is stolen.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                size="sm"
                onClick={() => { setShowProfile(true); setShow2FAConfirm(true); }}
                className="bg-[#3D2F08] hover:bg-[#5C4810] text-white rounded-sm text-xs h-7 font-medium"
                data-testid="enable-2fa-nudge-btn"
              >
                <Shield className="w-3 h-3 mr-1" /> Enable 2FA
              </Button>
              <button
                onClick={dismissTwoFANudge}
                className="text-[#8A6510] hover:text-[#3D2F08] p-1 rounded-sm hover:bg-[#F0DCA8]/40 transition-colors"
                aria-label="Dismiss"
                data-testid="dismiss-2fa-nudge-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-header with business selector */}
      <div className="bg-[#0A2B1D] text-white py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={selectedBusiness?.id || ''}
                onValueChange={(val) => {
                  const biz = businesses.find(b => b.id === val);
                  setSelectedBusiness(biz);
                }}
              >
                <SelectTrigger className="w-[200px] bg-white/10 border-white/20 text-white">
                  <Building2 className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Select business" />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map(biz => (
                    <SelectItem key={biz.id} value={biz.id}>
                      {biz.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button 
                variant="outline" 
                size="sm" 
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => setShowAddBusiness(true)}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Business
              </Button>

              {selectedBusiness && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => {
                    setEditBusinessName(selectedBusiness.name);
                    setEditBusinessNotifyEmail(selectedBusiness.notification_email || '');
                    setShowEditBusiness(true);
                  }}
                  data-testid="edit-business-btn"
                >
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => setSelectedYear(y => y - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-semibold text-lg min-w-[60px] text-center">{selectedYear}</span>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => setSelectedYear(y => y + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              <Select
                value={selectedMonth?.toString() || 'all'}
                onValueChange={(val) => setSelectedMonth(val === 'all' ? null : parseInt(val))}
              >
                <SelectTrigger className="w-[130px] bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map(m => (
                    <SelectItem key={m.value || 'all'} value={m.value?.toString() || 'all'}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {!selectedBusiness ? (
          <Card className="text-center py-16">
            <CardContent>
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Business Selected</h3>
              <p className="text-gray-500 mb-6">Create your first business to start tracking expenses and income.</p>
              <Button onClick={() => setShowAddBusiness(true)} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm">
                <Plus className="w-4 h-4 mr-2" /> Add Your First Business
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Bank Connection Card */}
            <Card className="mb-6 border-[#E6E8E6] bg-[#E2ECE7]/30">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#E2ECE7] rounded-sm">
                      <CreditCard className="w-5 h-5 text-[#0A2B1D]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#111614]">Bank Connections</h3>
                      <p className="text-sm text-[#5C6661]">
                        {connectedAccounts.length > 0 
                          ? `${connectedAccounts.length} account(s) connected`
                          : isPaidUser 
                            ? 'Connect your bank to auto-import transactions'
                            : 'Upgrade to Pro to connect your bank'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {connectedAccounts.length > 0 && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleSyncTransactions}
                          disabled={syncingTransactions}
                          className="border-[#E6E8E6] hover:bg-[#E2ECE7]"
                        >
                          {syncingTransactions ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          Sync
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleDisconnectBank}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid="disconnect-bank-btn"
                        >
                          Disconnect
                        </Button>
                      </>
                    )}
                    <Button 
                      size="sm" 
                      className={`rounded-sm ${isPaidUser ? 'bg-[#0A2B1D] hover:bg-[#254D3D]' : 'bg-[#8A948F] hover:bg-[#5C6661]'}`}
                      onClick={isPaidUser ? () => handleConnectBank() : () => setShowUpgradeDialog(true)}
                      data-testid="connect-bank-btn"
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      {!isPaidUser ? 'Upgrade to Connect' : connectedAccounts.length > 0 ? 'Add Account' : 'Connect Bank'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            {pnlReport && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="border-[#E6E8E6]">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-[#5C6661]">Total Income</p>
                        <p className="text-xl sm:text-2xl font-semibold text-[#3A6E53]">
                          {formatCurrency(pnlReport.summary?.total_income)}
                        </p>
                      </div>
                      <div className="p-2 sm:p-3 bg-[#E2ECE7] rounded-sm">
                        <ArrowUpRight className="w-5 h-5 sm:w-6 sm:h-6 text-[#3A6E53]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-[#E6E8E6]">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-[#5C6661]">Total Expenses</p>
                        <p className="text-xl sm:text-2xl font-semibold text-[#A33B31]">
                          {formatCurrency(pnlReport.summary?.total_expenses)}
                        </p>
                      </div>
                      <div className="p-2 sm:p-3 bg-red-50 rounded-sm">
                        <ArrowDownRight className="w-5 h-5 sm:w-6 sm:h-6 text-[#A33B31]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-[#E6E8E6]">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-[#5C6661]">Net Profit</p>
                        <p className={`text-xl sm:text-2xl font-semibold ${pnlReport.summary?.net_profit >= 0 ? 'text-[#3A6E53]' : 'text-[#A33B31]'}`}>
                          {formatCurrency(pnlReport.summary?.net_profit)}
                        </p>
                      </div>
                      <div className={`p-2 sm:p-3 rounded-sm ${pnlReport.summary?.net_profit >= 0 ? 'bg-[#E2ECE7]' : 'bg-red-50'}`}>
                        {pnlReport.summary?.net_profit >= 0 ? (
                          <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-[#3A6E53]" />
                        ) : (
                          <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-[#A33B31]" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-[#E6E8E6]">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm text-[#5C6661]">Profit Margin</p>
                        <p className={`text-xl sm:text-2xl font-semibold ${pnlReport.summary?.profit_margin >= 0 ? 'text-[#3A6E53]' : 'text-[#A33B31]'}`}>
                          {(pnlReport.summary?.profit_margin || 0).toFixed(1)}%
                        </p>
                      </div>
                      <div className="p-2 sm:p-3 bg-[#E2ECE7] rounded-sm">
                        <PieChart className="w-5 h-5 sm:w-6 sm:h-6 text-[#0A2B1D]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Main Content Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="flex flex-wrap gap-1 h-auto">
                <TabsTrigger value="overview" className="text-xs sm:text-sm" data-testid="overview-tab-trigger">
                  <DollarSign className="w-4 h-4 mr-1 hidden sm:inline" /> Overview
                </TabsTrigger>
                <TabsTrigger value="expenses" className="text-xs sm:text-sm">
                  <ArrowDownRight className="w-4 h-4 mr-1 hidden sm:inline" /> Expenses
                </TabsTrigger>
                <TabsTrigger value="income" className="text-xs sm:text-sm">
                  <ArrowUpRight className="w-4 h-4 mr-1 hidden sm:inline" /> Income
                </TabsTrigger>
                <TabsTrigger value="invoices" className="text-xs sm:text-sm">
                  <FileText className="w-4 h-4 mr-1 hidden sm:inline" /> Invoices
                </TabsTrigger>
                <TabsTrigger value="receipts" className="text-xs sm:text-sm">
                  <Receipt className="w-4 h-4 mr-1 hidden sm:inline" /> Receipts
                </TabsTrigger>
                <TabsTrigger value="mileage" className="text-xs sm:text-sm">
                  <Car className="w-4 h-4 mr-1 hidden sm:inline" /> Mileage
                </TabsTrigger>
                <TabsTrigger value="reports" className="text-xs sm:text-sm">
                  <PieChart className="w-4 h-4 mr-1 hidden sm:inline" /> Reports
                </TabsTrigger>
                <TabsTrigger value="forecast" className="text-xs sm:text-sm">
                  <BarChart3 className="w-4 h-4 mr-1 hidden sm:inline" /> Forecast
                </TabsTrigger>
                <TabsTrigger value="cfo" className="text-xs sm:text-sm" data-testid="cfo-tab-trigger">
                  <Sparkles className="w-4 h-4 mr-1 hidden sm:inline" /> CFO
                </TabsTrigger>
                <TabsTrigger value="taxes" className="text-xs sm:text-sm" data-testid="tax-tab-trigger">
                  Taxes
                </TabsTrigger>
                <TabsTrigger value="payroll" className="text-xs sm:text-sm" data-testid="payroll-tab-trigger">
                  Payroll
                </TabsTrigger>
                <TabsTrigger value="team" className="text-xs sm:text-sm" data-testid="team-tab-trigger">
                  Team
                </TabsTrigger>
                <TabsTrigger value="import" className="text-xs sm:text-sm">
                  <Upload className="w-4 h-4 mr-1 hidden sm:inline" /> Import
                </TabsTrigger>
                <TabsTrigger value="backontrack" className="text-xs sm:text-sm" data-testid="backontrack-tab-trigger">
                  <History className="w-4 h-4 mr-1 hidden sm:inline" /> Back on Track
                </TabsTrigger>
                {(['business', 'business_plus'].includes(user.subscription_status) || user.is_admin) && (
                  <TabsTrigger value="clientportal" className="text-xs sm:text-sm" data-testid="clientportal-tab-trigger">
                    <Globe className="w-4 h-4 mr-1 hidden sm:inline" /> Client Portal
                  </TabsTrigger>
                )}
                {user.is_admin && (
                  <TabsTrigger value="admin" className="text-xs sm:text-sm" data-testid="admin-tab-trigger">
                    <Shield className="w-4 h-4 mr-1 hidden sm:inline" /> Admin
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="overview">
                <OverviewTab 
                  businessId={selectedBusiness?.id}
                  token={token}
                  expenses={expenses}
                  income={income}
                  pnlReport={pnlReport}
                  connectedAccounts={connectedAccounts}
                />
              </TabsContent>

              {/* Expenses Tab */}
              <TabsContent value="expenses">
                <ExpensesListTab
                  expenses={expenses}
                  expenseCategories={categories.expense_categories || []}
                  formatCurrency={formatCurrency}
                  onAddExpense={() => setShowAddExpense(true)}
                  onEditExpense={openEditDialog}
                  onDeleteExpense={handleDeleteExpense}
                  onBulkRecategorize={handleBulkRecategorize}
                />
              </TabsContent>

              {/* Income Tab */}
              <TabsContent value="income">
                <IncomeListTab
                  income={income}
                  formatCurrency={formatCurrency}
                  onAddIncome={() => setShowAddIncome(true)}
                  onEditIncome={openEditDialog}
                  onDeleteIncome={handleDeleteIncome}
                />
              </TabsContent>

              {/* Invoices Tab */}
              <TabsContent value="invoices">
                <InvoicesTab 
                  businessId={selectedBusiness?.id}
                  token={token}
                  authHeaders={authHeaders}
                />
              </TabsContent>

              {/* Receipts Tab */}
              <TabsContent value="receipts">
                <ReceiptsTab 
                  businessId={selectedBusiness?.id}
                  token={token}
                  authHeaders={authHeaders}
                  categories={categories}
                />
              </TabsContent>

              {/* Reports Tab */}
              <TabsContent value="reports">
                <PnLReportsTab
                  pnlReport={pnlReport}
                  selectedYear={selectedYear}
                  businessName={selectedBusiness?.name}
                  months={months}
                  formatCurrency={formatCurrency}
                  baseCurrency={baseCurrency}
                  onBaseCurrencyChange={(currency) => { setBaseCurrency(currency); }}
                  onExportExpensesCSV={exportExpensesCSV}
                  onExportIncomeCSV={exportIncomeCSV}
                  onExportPnLCSV={exportPnLCSV}
                  onShareWithAccountant={() => setShowShareDialog(true)}
                />
              </TabsContent>

              {/* Forecast Tab - Cash Flow Projection */}
              <TabsContent value="forecast">
                <ForecastTab
                  cashflowForecast={cashflowForecast}
                  recurringItems={recurringItems}
                  formatCurrency={formatCurrency}
                  onAddExpense={() => setShowAddExpense(true)}
                  onAddIncome={() => setShowAddIncome(true)}
                />
              </TabsContent>

              {/* CFO Tab - Burn rate, runway, budget, anomalies */}
              <TabsContent value="cfo">
                <CFOTab
                  token={token}
                  selectedBusiness={selectedBusiness}
                  expenseCategories={categories.expense_categories || []}
                  formatCurrency={formatCurrency}
                />
              </TabsContent>

              {/* Mileage Tab */}
              <TabsContent value="mileage">
                <MileageTab 
                  businessId={selectedBusiness?.id}
                  token={token}
                  authHeaders={authHeaders}
                  selectedYear={selectedYear}
                />
              </TabsContent>

              {/* Import Tab */}
              <TabsContent value="import">
                <ImportTab 
                  businessId={selectedBusiness?.id}
                  token={token}
                  authHeaders={authHeaders}
                  onImportComplete={() => {
                    fetchExpenses();
                    fetchIncome();
                    fetchPnLReport();
                  }}
                />
              </TabsContent>

              <TabsContent value="taxes">
                <TaxTab businessId={selectedBusiness?.id} token={token} />
              </TabsContent>

              <TabsContent value="payroll">
                <PayrollTab businessId={selectedBusiness?.id} token={token} />
              </TabsContent>

              <TabsContent value="team">
                <TeamTab businessId={selectedBusiness?.id} token={token} />
              </TabsContent>

              <TabsContent value="backontrack">
                <BackOnTrackTab businessId={selectedBusiness?.id} token={token} />
              </TabsContent>

              <TabsContent value="clientportal">
                <ClientPortalTab
                  businessId={selectedBusiness?.id}
                  token={token}
                  businessName={selectedBusiness?.name}
                  subscriptionStatus={user.subscription_status}
                  isAdmin={user.is_admin}
                />
              </TabsContent>

              {user.is_admin && (
                <TabsContent value="admin">
                  <AdminTab token={token} businessId={selectedBusiness?.id} />
                </TabsContent>
              )}
            </Tabs>
          </>
        )}
      </div>

      {/* Add Business Dialog */}
      <Dialog open={showAddBusiness} onOpenChange={setShowAddBusiness}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Business</DialogTitle>
            <DialogDescription>Create a new business to track expenses and income.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Business Name</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="My Business"
              />
            </div>
            <div>
              <Label>Business Type</Label>
              <Select
                value={formData.type || ''}
                onValueChange={(val) => setFormData({...formData, type: val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {businessTypes.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddBusiness(false)}>Cancel</Button>
            <Button onClick={handleCreateBusiness} disabled={!formData.name || !formData.type} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Business Dialog */}
      <Dialog open={showEditBusiness} onOpenChange={setShowEditBusiness}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Business</DialogTitle>
            <DialogDescription>Update the business name and where its CFO emails go.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Business Name</Label>
              <Input
                value={editBusinessName}
                onChange={(e) => setEditBusinessName(e.target.value)}
                placeholder="Business name"
                data-testid="edit-business-name-input"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                Notification email
                <span className="text-xs font-normal text-[#8A948F]">(optional)</span>
              </Label>
              <Input
                type="email"
                value={editBusinessNotifyEmail}
                onChange={(e) => setEditBusinessNotifyEmail(e.target.value)}
                placeholder={user.email ? `Defaults to ${user.email}` : 'you@example.com'}
                data-testid="edit-business-email-input"
              />
              <p className="text-xs text-[#5C6661] mt-1.5 leading-snug">
                Weekly Finance Briefs and CFO alerts for this business will be sent here. Leave blank to use your account email.
              </p>
            </div>
            {selectedBusiness && (
              <div className="pt-2 border-t border-[#E6E8E6]">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSendBriefNow}
                  disabled={sendingBrief}
                  className="w-full rounded-sm"
                  data-testid="send-brief-now-btn"
                >
                  {sendingBrief ? 'Sending...' : 'Send weekly Finance Brief now (test)'}
                </Button>
                <p className="text-xs text-[#8A948F] mt-1.5">
                  Bypasses the 7-day cycle. Useful for testing per-business email routing.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditBusiness(false)}>Cancel</Button>
            <Button onClick={handleRenameBusiness} disabled={!editBusinessName.trim()} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" data-testid="save-business-name-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Dialog */}
      <ProfileDialog
        open={showProfile}
        onOpenChange={setShowProfile}
        user={user}
        profileForm={profileForm}
        setProfileForm={setProfileForm}
        passwordForm={passwordForm}
        setPasswordForm={setPasswordForm}
        profileLoading={profileLoading}
        showCurrentPassword={showCurrentPassword}
        setShowCurrentPassword={setShowCurrentPassword}
        showNewPassword={showNewPassword}
        setShowNewPassword={setShowNewPassword}
        twoFactorEnabled={twoFactorEnabled}
        show2FAConfirm={show2FAConfirm}
        setShow2FAConfirm={setShow2FAConfirm}
        twoFATogglePassword={twoFATogglePassword}
        setTwoFATogglePassword={setTwoFATogglePassword}
        isDemoProspect={isDemoProspect}
        onUpdateProfile={handleUpdateProfile}
        onChangePassword={handleChangePassword}
        onToggle2FA={handleToggle2FA}
        onDeleteAccount={handleDeleteAccountFn}
        weeklyBriefOptOut={user?.weekly_brief_opt_out || false}
        onToggleWeeklyBrief={async (optOut) => {
          try {
            await axios.patch(`${API}/api/mybookie/auth/preferences`,
              { weekly_brief_opt_out: optOut }, authHeaders);
            setUser((u) => u ? { ...u, weekly_brief_opt_out: optOut } : u);
            toast({
              title: optOut ? 'Weekly Brief paused' : 'Weekly Brief enabled',
              description: optOut ? "You won't receive the weekly AI Finance Brief by email." : "You'll receive the weekly AI Finance Brief every 7 days.",
            });
          } catch (err) {
            toast({ title: 'Update failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
          }
        }}
        onUpgrade={() => {
          setShowProfile(false);
          if (isDemoProspect) {
            setShowConvertDialog(true);
          } else {
            navigate(isMyBookieDomain() ? '/pricing' : '/mybookie/pricing');
          }
        }}
      />

      {/* Transaction Dialogs */}
      <AddExpenseDialog
        open={showAddExpense}
        onOpenChange={setShowAddExpense}
        formData={formData}
        setFormData={setFormData}
        categories={categories}
        onSubmit={handleCreateExpense}
        businessId={selectedBusiness?.id}
        token={token}
      />
      <AddIncomeDialog
        open={showAddIncome}
        onOpenChange={setShowAddIncome}
        formData={formData}
        setFormData={setFormData}
        categories={categories}
        onSubmit={handleCreateIncome}
      />
      <EditTransactionDialog
        editingItem={editingItem}
        setEditingItem={setEditingItem}
        editType={editType}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={handleSaveEdit}
        categories={categories}
        businessId={selectedBusiness?.id}
        token={token}
        onCategoriesChanged={() => selectedBusiness?.id && fetchCategories(selectedBusiness.id)}
      />

      {/* Connect Bank — handled directly via Plaid CDN */}
      <PWAInstallPrompt />

      {/* Upgrade to Pro Dialog */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        isDemoProspect={isDemoProspect}
        onUpgrade={handleUpgradeAction}
      />

      {/* Share with Accountant Dialog */}
      <ShareDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        accountantEmail={accountantEmail}
        setAccountantEmail={setAccountantEmail}
        copied={copied}
        summaryText={generateShareableSummary()}
        onCopy={copyToClipboard}
        onGenerateLink={handleGenerateShareLink}
      />

      {/* Convert Demo Account Dialog */}
      <ConvertDemoDialog
        open={showConvertDialog}
        onOpenChange={setShowConvertDialog}
        name={convertName}
        setName={setConvertName}
        password={convertPassword}
        setPassword={setConvertPassword}
        loading={convertLoading}
        onSubmit={handleConvertDemo}
      />

      {/* Finance Bot Floating Widget */}
      <FinanceBotWidget
        businessName={selectedBusiness?.name}
        onSendMessage={handleBotMessage}
      />
    </div>
  );
};

export default MyBookieDashboard;
